import React from "react";
import { Card, CardHeader, Col, CardBody, Button, Row } from "reactstrap";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import getChartColorsArray from "../../Components/Common/ChartsDynamicColor";
import { getStatsPaymentServicesQuery } from "../../queries";
import ReactEcharts from "echarts-for-react";

const getPieChartOption = (data, title, dataColors) => {
  const chartColors = getChartColorsArray(dataColors);
  return {
    title: {
      text: title,
      left: "center",
      textStyle: {
        fontSize: 14,
        fontWeight: 600,
      },
    },
    tooltip: { trigger: "item" },
    legend: {
      orient: "vertical",
      left: "left",
      textStyle: { color: "#858d98" },
    },
    color: chartColors,
    series: [
      {
        name: title,
        type: "pie",
        radius: "50%",
        data: data.map((item) => ({
          value: item.stats.totalAmountPayment / 100,
          name: item.serviceName,
        })),
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: "rgba(0, 0, 0, 0.5)",
          },
        },
      },
    ],
  };
};

const getBarChartOption = (data, title, dataColors) => {
  const chartColors = getChartColorsArray(dataColors);
  return {
    title: {
      text: title,
      left: "center",
      textStyle: {
        fontSize: 14,
        fontWeight: 600,
      },
    },
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
    },
    grid: {
      left: "3%",
      right: "4%",
      bottom: "3%",
      containLabel: true,
    },
    xAxis: {
      type: "category",
      data: data.map((item) => item.serviceName),
      axisLabel: { rotate: 30 },
    },
    yAxis: {
      type: "value",
    },
    color: chartColors,
    series: [
      {
        name: title,
        type: "bar",
        data: data.map((item) => item.stats.totalAmountPayment / 100),
      },
    ],
    plotOptions: {
      bar: {
        barHeight: "100%",
        distributed: true,
        horizontal: true,
        dataLabels: {
          position: "bottom",
        },
      },
    },
  };
};

const UsersByDevice = ({ dateRange }) => {
  const { t } = useTranslation();

  const { data: statsData } = useQuery(
    getStatsPaymentServicesQuery({
      fromCreatedAt: dateRange[0],
      toCreatedAt: dateRange[1],
      depth: 1,
    })
  );

  const { data: statsData2 } = useQuery(
    getStatsPaymentServicesQuery({
      fromCreatedAt: dateRange[0],
      toCreatedAt: dateRange[1],
      depth: 2,
    })
  );

  const { data: statsData3 } = useQuery(
    getStatsPaymentServicesQuery({
      fromCreatedAt: dateRange[0],
      toCreatedAt: dateRange[1],
      depth: 3,
    })
  );

  const stats = [
    { data: statsData?.data.data || [], type: "pie" },
    {
      data: statsData2?.data.data || [],
      type: "pie",
    },
    {
      data: statsData3?.data.data || [],
      type: "bar", // changed from "treemap"
    },
  ];

  const handleExportExcel = () => {
    const hasData = stats.some((s) => s.data.length > 0);
    if (!hasData) {
      alert(t("No data available to export"));
      return;
    }

    const workbook = XLSX.utils.book_new();

    stats.forEach((depthSet, index) => {
      const sheetData = depthSet.data.map((item) => ({
        Service: item.serviceName,
        [t("Payments")]: item.stats.totalAmountPayment / 100,
        [t("Commissions")]: item.stats.totalAmountCommissions / 100,
        [t("Transactions")]: item.stats.transactionsCount,
      }));
      const sheet = XLSX.utils.json_to_sheet(sheetData);
      XLSX.utils.book_append_sheet(workbook, sheet, `Depth_${index + 1}`);
    });

    const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });

    saveAs(
      blob,
      `PaymentServicesStats_${new Date().toISOString().slice(0, 10)}.xlsx`
    );
  };

  return (
    <Row>
      <Col xl={12}>
        <Card className="card-height-100">
          <CardHeader className="d-flex align-items-center justify-content-between">
            <h3
              className="card-title mb-0"
              style={{ fontSize: "16px", fontWeight: 700 }}
            >
              {t("Stats by Payment Services")}
            </h3>
            <Button color="primary" size="sm" onClick={handleExportExcel}>
              <i className="ri-file-excel-2-line"></i> {t("Export")}
            </Button>
          </CardHeader>
          <CardBody>
            <Row>
              {stats.map((depth, idx) => (
                <Col xl={4} md={6} key={idx}>
                  <h6 className="text-center mb-2">{depth.title}</h6>
                  {depth.type === "pie" ? (
                    <div>
                      <ReactEcharts
                        style={{ height: "300px" }}
                        option={getPieChartOption(
                          depth.data,
                          depth.title,
                          '["--vz-primary", "--vz-warning", "--vz-info", "--vz-success", "--vz-danger"]'
                        )}
                      />
                      <div className="mt-4">
                        <div className="table-responsive mt-2">
                          <table className="table table-borderless table-sm table-centered align-middle table-nowrap mb-0">
                            <thead>
                              <tr>
                                <th>{t("Service")}</th>
                                <th>{t("Payments")}</th>
                                <th>{t("Commissions")}</th>
                                <th>{t("Transactions")}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(idx === 0
                                ? statsData
                                : statsData2
                              )?.data?.data?.map((item) => (
                                <tr key={item.serviceName}>
                                  <td>{item.serviceName}</td>
                                  <td>
                                    {(
                                      item.stats.totalAmountPayment / 100
                                    ).toLocaleString()}
                                  </td>
                                  <td>
                                    {(
                                      item.stats.totalAmountCommissions / 100
                                    ).toLocaleString()}
                                  </td>
                                  <td>{item.stats.transactionsCount}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <ReactEcharts
                        style={{ height: "300px" }}
                        option={getBarChartOption(
                          depth.data,
                          depth.title,
                          '["--vz-primary", "--vz-warning", "--vz-info", "--vz-success", "--vz-danger"]'
                        )}
                      />
                      <div className="mt-4">
                        <div className="table-responsive mt-2">
                          <table className="table table-borderless table-sm table-centered align-middle table-nowrap mb-0">
                            <thead>
                              <tr>
                                <th>{t("Service")}</th>
                                <th>{t("Payments")}</th>
                                <th>{t("Commissions")}</th>
                                <th>{t("Transactions")}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {statsData3?.data?.data?.map((item) => (
                                <tr key={item.serviceName}>
                                  <td>{item.serviceName}</td>
                                  <td>
                                    {(
                                      item.stats.totalAmountPayment / 100
                                    ).toLocaleString()}
                                  </td>
                                  <td>
                                    {(
                                      item.stats.totalAmountCommissions / 100
                                    ).toLocaleString()}
                                  </td>
                                  <td>{item.stats.transactionsCount}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}
                </Col>
              ))}
            </Row>
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

export default UsersByDevice;
